/**
 * OCTOPUS - LiveSourceCard (item 6.17; redesign 2026-07-03)
 * Stat tile de um sinal em tempo real.
 *
 * Camada de APRESENTAÇÃO pura (INV-UI-TRAIL-001 / INV-LAYER-READPATH-001):
 * o valor exibido (`formatted`/`display_value`) já chega convertido do backend
 * (live-view). Este componente NÃO converte unidades nem recalcula telemetria —
 * apenas escolhe a forma visual (tile, medidor, gauge radial, bússola) e
 * respeita min/max/casas decimais do cadastro, com fallback genérico.
 *
 * Anatomia do tile (contrato de stat tile): rótulo em caixa de frase, valor
 * semibold com a unidade menor ao lado, medidor fino opcional (fill carrega a
 * severidade; trilho mais claro). Estado (inválido/desatualizado) sempre com
 * ícone + tooltip, nunca só cor.
 */

import React, { useMemo } from 'react';
import { AlertTriangle, Clock, Crosshair, Timer } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import GaugeRadial from '@/components/widgets/GaugeRadial';
import CompassRose from '@/components/widgets/CompassRose';

// Escolha da FORMA visual por path (decisão de apresentação, não de dados)
const inferWidgetType = (path) => {
  if (!path) return 'tile';
  if (path === 'navigation.position') return 'position';
  const pathLower = path.toLowerCase();

  if (pathLower.includes('runtime')) return 'runtime';              // Horímetro
  if (pathLower.includes('revolutions')) return 'gauge_radial';     // RPM
  if (pathLower.includes('speedoverground')) return 'gauge_radial'; // Velocidade
  if (pathLower.includes('courseoverground')) return 'compass';     // COG
  if (pathLower.includes('heading')) return 'compass';              // Proa
  return 'tile';
};

// Famílias com faixa conhecida ganham o medidor fino (meter) no tile.
const hasMeter = (path) => {
  if (!path) return false;
  const p = path.toLowerCase();
  return (
    p.includes('temperature') ||
    p.includes('pressure') ||
    p.includes('fuel.rate') ||
    p.includes('voltage') ||
    p.includes('currentlevel') ||
    p.includes('engineload') ||
    p.includes('stateofcharge') ||
    p.includes('depth')
  );
};

// Fallback GENÉRICO de faixa quando o cadastro (min/max do source_config) não
// definiu — usado só como último recurso, por família de sinal.
const getFallbackMaxForPath = (path) => {
  if (!path) return 100;
  const pathLower = path.toLowerCase();

  if (pathLower.includes('revolutions')) return 3000;
  if (pathLower.includes('speedoverground')) return 40;   // km/h (default live-view)
  if (pathLower.includes('fuel.rate')) return 100;
  if (pathLower.includes('boostpressure')) return 3;      // bar
  if (pathLower.includes('pressure')) return 10;          // bar
  if (pathLower.includes('voltage')) return 30;
  if (pathLower.includes('currentlevel')) return 100;
  if (pathLower.includes('engineload')) return 100;
  if (pathLower.includes('stateofcharge')) return 100;
  if (pathLower.includes('temperature')) return 120;
  if (pathLower.includes('depth')) return 50;
  return 100;
};

const SEVERITY_FILL = {
  critical: 'bg-red-500',
  warning: 'bg-amber-500',
};

const LiveSourceCard = ({
  path,
  displayName,
  displayValue,
  displayUnit,
  formatted,
  stale = false,
  suspect = false,
  sourceConflict = false,
  alertZone = null,
  decimalPlaces = null,
  minValue = null,
  maxValue = null,
}) => {
  const widgetType = useMemo(() => inferWidgetType(path), [path]);

  // Zona de alerta pode chegar como objeto ({severity}) do live-view ou como
  // string (compat sources-live antigo).
  const alertSeverity = typeof alertZone === 'string' ? alertZone : alertZone?.severity;

  const decimals = Number.isFinite(decimalPlaces) ? decimalPlaces : 1;
  const gaugeMin = Number.isFinite(minValue) ? minValue : 0;
  const gaugeMax = Number.isFinite(maxValue) ? maxValue : getFallbackMaxForPath(path);

  const isNumeric = typeof displayValue === 'number' && Number.isFinite(displayValue);
  const numericValue = isNumeric ? displayValue : 0;

  // Texto do valor SEM a unidade (a unidade vira sufixo menor no tile).
  // `formatted` do backend = "83.3 °C" — remover o sufixo é só apresentação.
  const valueText = useMemo(() => {
    if (suspect) return '—';
    const full = formatted && formatted !== '' ? formatted : null;
    if (full == null) {
      return displayValue !== null && displayValue !== undefined ? String(displayValue) : '—';
    }
    if (displayUnit && full.endsWith(` ${displayUnit}`)) {
      return full.slice(0, -displayUnit.length - 1);
    }
    return full;
  }, [formatted, displayValue, displayUnit, suspect]);

  const showUnit = !suspect && Boolean(displayUnit) && valueText !== '—';

  // Medidor fino: fill carrega severidade; trilho = tom mais claro do mesmo eixo.
  const meterPct = useMemo(() => {
    if (!isNumeric || gaugeMax <= gaugeMin) return null;
    const pct = ((numericValue - gaugeMin) / (gaugeMax - gaugeMin)) * 100;
    return Math.max(0, Math.min(100, pct));
  }, [isNumeric, numericValue, gaugeMin, gaugeMax]);

  const showMeter = !suspect && hasMeter(path) && meterPct !== null;
  const fillClass = SEVERITY_FILL[alertSeverity] || 'bg-primary';

  const statusBadges = (
    <span className="flex items-center gap-1 shrink-0">
      {sourceConflict && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center" data-testid="live-conflict-badge">
              <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[240px]">
            <p>
              Duas fontes físicas publicam este sinal e não há resolução no
              cadastro — o valor foi suprimido para não misturar motores.
              Resolva a fonte em Equipamentos → Fontes.
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      {suspect && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center" data-testid="live-suspect-badge">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="max-w-[220px]">
            <p>
              Leitura fora da faixa física plausível — possível falha do sensor ou
              ruído do barramento. O valor não é exibido.
            </p>
          </TooltipContent>
        </Tooltip>
      )}
      {stale && (
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex items-center">
              <Clock className="h-3.5 w-3.5 text-muted-foreground" />
            </span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Dado desatualizado (&gt;5min)</p>
          </TooltipContent>
        </Tooltip>
      )}
    </span>
  );

  const label = (icon) => (
    <div className="flex items-start justify-between gap-2 min-w-0">
      <p
        className="text-xs text-muted-foreground leading-snug line-clamp-2 min-w-0 flex items-center gap-1"
        title={displayName || path}
      >
        {icon}
        <span className="truncate">{displayName || path?.split('.').pop() || 'Sinal'}</span>
      </p>
      {statusBadges}
    </div>
  );

  const tileBase = cn(
    'rounded-xl border bg-card p-3 flex flex-col gap-1.5 transition-colors',
    'hover:bg-accent/30',
    alertSeverity === 'critical' && 'border-l-2 border-l-red-500',
    alertSeverity === 'warning' && 'border-l-2 border-l-amber-500',
    stale && 'opacity-60',
  );

  if (widgetType === 'gauge_radial' || widgetType === 'compass') {
    return (
      <TooltipProvider>
        <div className={cn(tileBase, 'items-center row-span-2')} data-widget={widgetType}>
          <div className="w-full">{label()}</div>
          <div className="flex-1 flex items-center justify-center py-1">
            {widgetType === 'gauge_radial' ? (
              <GaugeRadial
                value={numericValue}
                min={gaugeMin}
                max={gaugeMax}
                unit={displayUnit || ''}
                showLabel={false}
                size={116}
                decimalPlaces={decimals}
              />
            ) : (
              <CompassRose
                value={numericValue}
                showLabel={false}
                showDegrees={true}
                size={116}
              />
            )}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  if (widgetType === 'position') {
    return (
      <TooltipProvider>
        <div className={cn(tileBase, 'col-span-2')} data-widget="position">
          {label(<Crosshair className="h-3 w-3 shrink-0" />)}
          <p className="font-mono text-sm text-foreground break-all">
            {suspect ? '—' : (formatted || '—')}
          </p>
        </div>
      </TooltipProvider>
    );
  }

  // 'runtime' e 'tile' compartilham a anatomia; runtime só ganha o ícone.
  return (
    <TooltipProvider>
      <div className={tileBase} data-widget={widgetType}>
        {label(widgetType === 'runtime' ? <Timer className="h-3 w-3 shrink-0" /> : undefined)}
        <div className="flex items-baseline gap-1 min-w-0">
          <span
            className={cn(
              'text-xl font-semibold leading-none truncate',
              suspect || valueText === '—' ? 'text-muted-foreground' : 'text-foreground',
            )}
          >
            {valueText}
          </span>
          {showUnit && (
            <span className="text-xs text-muted-foreground shrink-0">{displayUnit}</span>
          )}
        </div>
        {showMeter && (
          <div className="h-1 w-full rounded-full bg-muted overflow-hidden" aria-hidden="true">
            <div
              className={cn('h-full rounded-full transition-all', fillClass)}
              style={{ width: `${meterPct}%` }}
            />
          </div>
        )}
      </div>
    </TooltipProvider>
  );
};

export default LiveSourceCard;
